import { z } from "zod";
import { Created, Id, Ok, OptionalText } from "./common";
import { defineRoutes } from "./define";
import { PROBLEMS } from "./problem";

/** Mirrors core/modules/files/domain/file-type.ts. */
export const FILE_TYPES = [
  "photo",
  "video",
  "mri",
  "ultrasound",
  "xray",
  "pdf",
  "training_screenshot",
  "movement_analysis",
  "other",
] as const;
export const FileType = z.enum(FILE_TYPES);

export const FileListItem = z.object({
  id: Id,
  title: z.string(),
  type: FileType,
  /** An external link, or the API's own `/files/content/<key>` path for uploaded media. */
  href: z.string().nullable(),
  comment: z.string().nullable(),
  addedAt: z.string(),
});
export type FileListItem = z.infer<typeof FileListItem>;

/**
 * Text fields of the multipart upload (`multipart/form-data`): `file` (the binary part,
 * optional when `url` is given) plus these. Validated by the controller, not the contract
 * interceptor — the contract layer validates JSON only.
 */
export const UploadFileFields = z.object({
  title: z.string().trim().min(1, "Назва файлу обовʼязкова").max(200),
  episodeId: OptionalText(64),
  type: FileType.optional(),
  url: OptionalText(2000),
});
export type UploadFileFields = z.infer<typeof UploadFileFields>;

export const UpdateFileBody = z.object({
  title: z.string().trim().min(1, "Назва файлу обовʼязкова").max(200),
  type: FileType.optional(),
  comment: OptionalText(2000),
});
export type UpdateFileBody = z.infer<typeof UpdateFileBody>;

export const files = defineRoutes({
  list: {
    method: "GET",
    path: "/patients/:patientId/files",
    summary: "A patient's files, newest first (clinical content)",
    params: z.object({ patientId: Id }),
    responses: { 200: z.array(FileListItem), 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  upload: {
    method: "POST",
    path: "/patients/:patientId/files",
    summary:
      "Attach a file or a link: multipart/form-data with `file` (≤ 50 MB) and/or `url`, plus `title`, optional `episodeId`, `type` (see UploadFileFields)",
    params: z.object({ patientId: Id }),
    responses: {
      201: Created,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      413: PROBLEMS[400],
      422: PROBLEMS[422],
    },
  },
  update: {
    method: "PATCH",
    path: "/files/:fileId",
    summary: "Re-title / re-categorise a file",
    params: z.object({ fileId: Id }),
    body: UpdateFileBody,
    responses: {
      200: Ok,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  delete: {
    method: "DELETE",
    path: "/files/:fileId",
    summary: "Soft-delete a file (the stored object is retained)",
    params: z.object({ fileId: Id }),
    responses: { 200: Ok, 401: PROBLEMS[401], 403: PROBLEMS[403], 404: PROBLEMS[404] },
  },
  download: {
    method: "GET",
    path: "/files/content/*key",
    summary:
      "Stream a stored object by its storage key (binary body with its content type; not JSON — use fetch, not the typed client)",
    params: z.object({ key: z.union([z.string(), z.array(z.string())]) }),
    responses: { 200: z.unknown(), 401: PROBLEMS[401], 403: PROBLEMS[403], 404: PROBLEMS[404] },
  },
});
